import { ellipse, INK, paint, type Ctx } from "./util.ts";

type Kind = "feather" | "dust" | "spark" | "text" | "yolk" | "shout" | "plus" | "z";

interface Particle {
  kind: Kind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  rot: number;
  vr: number;
  size: number;
  color: string;
  text?: string;
}

interface Projectile {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  t0: number;
  dur: number;
  resolve: () => void;
}

export class Fx {
  private ps: Particle[] = [];
  private eggs: Projectile[] = [];
  private shakeAmt = 0;

  private add(p: Partial<Particle> & Pick<Particle, "kind" | "x" | "y">): void {
    this.ps.push({ vx: 0, vy: 0, life: 1, max: 1, rot: 0, vr: 0, size: 1, color: "#fff", ...p });
  }

  feathers(x: number, y: number, color: string, n: number): void {
    for (let i = 0; i < n; i++) {
      const life = 1.2 + Math.random() * 0.9;
      this.add({ kind: "feather", x, y, vx: (Math.random() - 0.5) * 260, vy: -120 - Math.random() * 200, life, max: life, rot: Math.random() * 6, vr: (Math.random() - 0.5) * 8, size: 0.7 + Math.random() * 0.6, color });
    }
  }

  dust(x: number, y: number, n = 6): void {
    for (let i = 0; i < n; i++) {
      this.add({ kind: "dust", x: x + (Math.random() - 0.5) * 40, y, vx: (Math.random() - 0.5) * 80, vy: -20 - Math.random() * 30, life: 0.6, max: 0.6, size: 6 + Math.random() * 8, color: "rgba(214,190,150," });
    }
  }

  sparks(x: number, y: number, n = 10): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = 150 + Math.random() * 250;
      this.add({ kind: "spark", x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0.35, max: 0.35, color: Math.random() < 0.5 ? "#fff6b0" : "#ffcf3a" });
    }
  }

  yolk(x: number, y: number): void {
    for (let i = 0; i < 14; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = 80 + Math.random() * 200;
      this.add({ kind: "yolk", x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 80, life: 0.7, max: 0.7, size: 3 + Math.random() * 5, color: Math.random() < 0.6 ? "#ffb81f" : "#fff8e8" });
    }
  }

  text(x: number, y: number, text: string, color: string, size = 34): void {
    this.add({ kind: "text", x, y, vy: -70, life: 1.1, max: 1.1, size, color, text });
  }

  shout(x: number, y: number, dir: number, text: string): void {
    this.add({ kind: "shout", x, y, vx: dir * 40, vy: -30, life: 0.9, max: 0.9, size: 1, color: "#fff", text });
  }

  heal(x: number, y: number): void {
    for (let i = 0; i < 12; i++) {
      this.add({ kind: "plus", x: x + (Math.random() - 0.5) * 70, y: y - Math.random() * 60, vy: -60 - Math.random() * 40, life: 1, max: 1, size: 6 + Math.random() * 6, color: "#6be37a" });
    }
  }

  snooze(x: number, y: number): void {
    for (let i = 0; i < 3; i++) {
      this.add({ kind: "z", x: x + i * 10, y: y - i * 14, vx: 20, vy: -40, life: 1 + i * 0.2, max: 1 + i * 0.2, size: 16 + i * 6, color: "#fff", text: "z" });
    }
  }

  shake(amount: number): void {
    this.shakeAmt = Math.max(this.shakeAmt, amount);
  }

  /** Lobs an egg; resolves when it lands. */
  egg(x0: number, y0: number, x1: number, y1: number, now: number): Promise<void> {
    const { promise, resolve } = Promise.withResolvers<void>();
    this.eggs.push({ x0, y0, x1, y1, t0: now, dur: 0.55 + Math.abs(x1 - x0) / 2400, resolve });
    return promise;
  }

  /** Screen-shake offset for this frame. */
  offset(): [number, number] {
    if (this.shakeAmt < 0.5) return [0, 0];
    return [(Math.random() - 0.5) * this.shakeAmt, (Math.random() - 0.5) * this.shakeAmt];
  }

  draw(ctx: Ctx, now: number, dt: number): void {
    this.shakeAmt *= Math.pow(0.02, dt);
    for (const e of [...this.eggs]) {
      const t = Math.min(1, (now - e.t0) / e.dur);
      const x = e.x0 + (e.x1 - e.x0) * t;
      const y = e.y0 + (e.y1 - e.y0) * t - Math.sin(t * Math.PI) * 140;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(now * 14);
      ellipse(ctx, 0, 0, 9, 12);
      paint(ctx, "#fff8e8", 2.5);
      ctx.restore();
      if (t >= 1) {
        this.eggs.splice(this.eggs.indexOf(e), 1);
        e.resolve();
      }
    }

    const alive: Particle[] = [];
    for (const p of this.ps) {
      p.life -= dt;
      if (p.life <= 0) continue;
      alive.push(p);
      const k = p.life / p.max;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      switch (p.kind) {
        case "feather":
          p.vx *= Math.pow(0.15, dt);
          p.vy = Math.min(p.vy + 420 * dt, 50);
          p.x += Math.sin(p.life * 6) * 30 * dt;
          ctx.save();
          ctx.globalAlpha = Math.min(1, k * 2);
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.scale(p.size, p.size);
          ctx.beginPath();
          ctx.moveTo(-10, 0);
          ctx.quadraticCurveTo(0, -6, 10, 0);
          ctx.quadraticCurveTo(0, 6, -10, 0);
          paint(ctx, p.color, 1.5);
          ctx.restore();
          break;
        case "dust":
          ctx.fillStyle = `${p.color}${(k * 0.6).toFixed(2)})`;
          ellipse(ctx, p.x, p.y, p.size * (2 - k), p.size * (2 - k) * 0.7);
          ctx.fill();
          break;
        case "spark":
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.vx * 0.04, p.y - p.vy * 0.04);
          ctx.stroke();
          break;
        case "yolk":
          p.vy += 600 * dt;
          ctx.globalAlpha = Math.min(1, k * 2);
          ellipse(ctx, p.x, p.y, p.size, p.size);
          ctx.fillStyle = p.color;
          ctx.fill();
          ctx.globalAlpha = 1;
          break;
        case "text":
        case "shout":
        case "z": {
          p.vy *= Math.pow(0.3, dt);
          const pop = p.kind === "text" ? 1 + Math.max(0, (k - 0.85) * 4) : 1;
          ctx.save();
          ctx.globalAlpha = Math.min(1, k * 2.5);
          ctx.translate(p.x, p.y);
          ctx.scale(pop, pop);
          ctx.font = `${p.kind === "shout" ? 30 : p.size}px "Luckiest Guy", Impact, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.lineJoin = "round";
          ctx.lineWidth = 7;
          ctx.strokeStyle = INK;
          ctx.strokeText(p.text ?? "", 0, 0);
          ctx.fillStyle = p.color;
          ctx.fillText(p.text ?? "", 0, 0);
          ctx.restore();
          break;
        }
        case "plus":
          ctx.globalAlpha = Math.min(1, k * 2);
          ctx.fillStyle = p.color;
          ctx.fillRect(p.x - p.size / 2, p.y - p.size / 6, p.size, p.size / 3);
          ctx.fillRect(p.x - p.size / 6, p.y - p.size / 2, p.size / 3, p.size);
          ctx.globalAlpha = 1;
          break;
      }
    }
    this.ps = alive;
  }
}
